import { useCallback, useEffect, useRef } from 'react'
import { api } from '../api/client.js'
import { useAuthStore } from '../store/authStore.js'
import { useConversationsStore } from '../store/conversationsStore.js'
import type {
  ConversationFilters,
  ConversationListResponse,
} from '../modules/conversations/types.js'

function buildQueryString(
  filters: ConversationFilters,
  agentId: string,
  offset: number,
): string {
  const params = new URLSearchParams()
  if (filters.status !== 'all') params.set('status', filters.status)
  for (const ch of filters.channels) params.append('channel', ch)
  if (filters.assignee === 'me-and-queues') {
    params.set('assignedAgentIdOrQueueMember', agentId)
  } else if (filters.assignee === 'me-only') {
    params.set('assignedAgentId', agentId)
  } else if (filters.assignee === 'unassigned') {
    params.set('assignedAgentId', 'null')
  }
  if (filters.searchQuery.trim()) {
    params.set('search', filters.searchQuery.trim())
  }
  params.set('limit', String(Math.min(Math.max(1, filters.limit), 200)))
  params.set('offset', String(offset))
  return params.toString()
}

export interface UseConversationsReturn {
  goToPage: (page: number) => Promise<void>
  refetch: () => Promise<void>
}

// Module-level singleton ref to the latest mounted hook instance's API.
let current: UseConversationsReturn | null = null

export function useConversations(): UseConversationsReturn {
  const filters = useConversationsStore((s) => s.filters)
  const page = useConversationsStore((s) => s.page)
  const agentId = useAuthStore((s) => s.user?.sub) ?? ''
  const filtersRef = useRef(filters)
  const pageRef = useRef(page)
  const hasAutoSelectedRef = useRef(false)

  const doFetch = useCallback(
    async (targetPage: number): Promise<void> => {
      if (!agentId) return
      const store = useConversationsStore.getState()
      const limit = store.filters.limit
      const offset = (targetPage - 1) * limit

      store.setLoading(true)
      store.setError(null)
      try {
        const qs = buildQueryString(filtersRef.current, agentId, offset)
        const resp = await api.get<ConversationListResponse>(`/conversations?${qs}`)
        store.setList(resp.items, resp.nextOffset, resp.totalCount)
        store.setRatingScale(resp.ratingScale)

        // Auto-select first item on initial load only
        if (
          !hasAutoSelectedRef.current &&
          resp.items.length > 0 &&
          store.selectedId === null
        ) {
          store.setSelectedId(resp.items[0]!.id)
          hasAutoSelectedRef.current = true
        }
      } catch (err) {
        store.setError(`Failed to load conversations: ${(err as Error).message}`)
      } finally {
        store.setLoading(false)
      }
    },
    [agentId],
  )

  // Refetch on filter or page change
  useEffect(() => {
    filtersRef.current = filters
    pageRef.current = page
    void doFetch(page)
  }, [filters, page, doFetch])

  const goToPage = useCallback(async (targetPage: number): Promise<void> => {
    const store = useConversationsStore.getState()
    store.setPage(targetPage)
    // doFetch will be triggered by the useEffect above
  }, [])

  const refetch = useCallback(async (): Promise<void> => {
    const store = useConversationsStore.getState()
    await doFetch(store.page)
  }, [doFetch])

  const apiRef = useRef<UseConversationsReturn>({ goToPage, refetch })
  apiRef.current.goToPage = goToPage
  apiRef.current.refetch = refetch

  useEffect(() => {
    current = apiRef.current
    return () => {
      if (current === apiRef.current) current = null
    }
  }, [])

  return apiRef.current
}

useConversations.getCurrent = (): UseConversationsReturn | null => current
